import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { Group, Grouping, useMetadata } from '../../contexts/MetadataContext';
import MemoizedGroup from './components/MemoziedGroup';
import { debounce } from 'lodash';
import EditFormScrollProvider from './components/EditFormScrollProvider';
import { useLocation } from 'react-router-dom';
import { ScrollRestoration, useScroll } from '../../contexts/ScrollProvider';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../flags';

interface EditProps {
  propertyType: string;
  onActiveChange: (group: string, section: string) => void;
  isProgrammaticScroll: boolean;
}

const HEADER_HEIGHT = 180;
const SCROLL_THRESHOLD = 50;
const NAV_SELECTOR = '[data-nav-sections]';

const Edit: React.FC<EditProps> = ({
  propertyType,
  onActiveChange,
  isProgrammaticScroll,
}) => {
  const location = useLocation();
  const { template } = useMetadata();
  const { hasFeature, getFeatureValue } = useFeatureFlags();
  const { saveEditFormPosition, getEditFormPosition } = useScroll();
  const [templateData, setTemplateData] = useState<{
    groupings: Grouping[];
    groups: Group[];
  }>({
    groupings: [],
    groups: [],
  });
  const savedPosition = useMemo(() => getEditFormPosition(), []);
  const isRestoringScroll =
    location.state?.from === 'media' && savedPosition > 0;

  // Separate effect to track position for media navigation
  useEffect(() => {
    const trackScrollForMedia = () => {
      saveEditFormPosition(window.scrollY);
    };

    window.addEventListener('scroll', trackScrollForMedia, { passive: true });

    return () => {
      window.removeEventListener('scroll', trackScrollForMedia);
    };
  }, [saveEditFormPosition]);

  const mediaScrollOffset = Number(
    hasFeature(FeatureFlags.NGW_MEDIA_SCROLL_OFFSET)
      ? getFeatureValue(FeatureFlags.NGW_MEDIA_SCROLL_OFFSET, true) || 20
      : 20,
  );

  const v2 = hasFeature(FeatureFlags.NGW_EDIT_REDESIGN);

  const isFirstLoadRef = useRef(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const lastScrollPosition = useRef(0);

  // Function to get mapping of sections to their group index
  const getSectionToGroupMap = useCallback(() => {
    const map = new Map<
      string,
      { groupName: string; isFirstInGroup: boolean }
    >();

    templateData.groups.forEach((group, groupIndex) => {
      if (group.sections && group.sections.length > 0) {
        const isLastGroup = groupIndex === templateData.groups.length - 1;
        group.sections
          .filter((o) => !!o.name)
          .forEach((section, index, sections) => {
            map.set(section.name, {
              groupName: group.name,
              isFirstInGroup:
                index === 0 && !(isLastGroup && sections.length < 2),
            });
          });
      }
    });

    return map;
  }, [templateData.groups]);

  // Function to scroll nav item into view
  const scrollNavItemIntoView = useCallback((selector: string) => {
    const navContainer = document.querySelector(NAV_SELECTOR);
    const navItem = navContainer?.querySelector(selector);

    if (navContainer && navItem) {
      navItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  // Function to get all visible sections
  const getVisibleSections = useCallback(() => {
    return Object.entries(sectionRefs.current)
      .filter(([_, ref]) => ref !== null)
      .map(([key, ref]) => {
        const rect = ref!.getBoundingClientRect();
        const offsetTop = rect.top + window.scrollY;
        return {
          key,
          offsetTop,
          height: rect.height,
          ref,
        };
      })
      .sort((a, b) => a.offsetTop - b.offsetTop);
  }, []);

  const handleSectionChange = useCallback(
    (groupName: string, sectionName: string) => {
      const sectionToGroupMap = getSectionToGroupMap();
      const sectionInfo = sectionToGroupMap.get(sectionName);

      if (isProgrammaticScroll) {
        return;
      }

      // If this is the first section in its group, we want to make sure the group is visible
      if (
        sectionInfo &&
        (sectionInfo.isFirstInGroup || groupName !== sectionInfo.groupName)
      ) {
        onActiveChange(sectionInfo.groupName, sectionName);
        scrollNavItemIntoView(`[data-group-id="${groupName}"]`);
      } else {
        onActiveChange(groupName, sectionName);
        scrollNavItemIntoView(`[data-section-id="${sectionName}"]`);
      }
    },
    [
      onActiveChange,
      scrollNavItemIntoView,
      getSectionToGroupMap,
      isProgrammaticScroll,
    ],
  );

  const handleScroll = useCallback(
    (isProgrammaticScroll: boolean) => {
      const scrollPosition = window.scrollY;

      saveEditFormPosition(scrollPosition);

      if (isProgrammaticScroll || !containerRef.current) return;

      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const visibleSections = getVisibleSections();

      if (!visibleSections.length) return;

      // Handle scroll to top
      if (scrollPosition === 0) {
        const firstSection = visibleSections[0];
        const [groupName, sectionName] = firstSection.key.split('|');
        handleSectionChange(groupName, sectionName);
        return;
      }

      // Handle scroll to bottom
      if (
        scrollPosition + viewportHeight >=
        documentHeight - SCROLL_THRESHOLD
      ) {
        const lastSection = visibleSections[visibleSections.length - 1];
        const [groupName, sectionName] = lastSection.key.split('|');
        handleSectionChange(groupName, sectionName);
        return;
      }

      // Handle normal scroll
      const scrollPosition1 = window.scrollY + HEADER_HEIGHT + SCROLL_THRESHOLD;
      let currentGroup = '';
      let currentSection = '';

      visibleSections.forEach(({ key, offsetTop, height }) => {
        if (
          scrollPosition1 >= offsetTop &&
          scrollPosition1 < offsetTop + height
        ) {
          const [groupName, sectionName] = key.split('|');
          currentGroup = groupName;
          currentSection = sectionName;
        }
      });

      if (currentGroup && currentSection) {
        handleSectionChange(currentGroup, currentSection);
      }

      lastScrollPosition.current = scrollPosition;
    },
    [handleSectionChange, getVisibleSections],
  );

  useEffect(() => {
    const data = template(propertyType);
    setTemplateData({ groupings: data.groupings || [], groups: data.groups });
    return () => {
      isFirstLoadRef.current = false;
    };
  }, [template, propertyType]);

  const handleCustomControlLoaded = useCallback(
    debounce((_groupIndex: number, _fieldKey: string) => {
      // Custom control loading logic
    }, 100),
    [],
  );

  const memoizedGroups = useMemo(() => {
    return templateData.groups.map((group: Group, index: number) => (
      <MemoizedGroup
        v2={v2}
        key={`${group.name}-${index}`}
        groupings={templateData.groupings}
        group={group}
        groupIndex={index}
        onCustomControlLoaded={handleCustomControlLoaded}
        sectionRefs={sectionRefs}
      />
    ));
  }, [templateData, handleCustomControlLoaded, v2]);

  return (
    <ScrollRestoration
      scrollOffset={mediaScrollOffset}
      isRestoring={isRestoringScroll}
    >
      <EditFormScrollProvider
        enabled={!isProgrammaticScroll}
        onScroll={() => handleScroll(isProgrammaticScroll)}
        headerHeight={HEADER_HEIGHT}
        isFirstLoad={isFirstLoadRef.current}
      >
        <Box
          ref={containerRef}
          sx={{
            height: '100%',
            overflowX: 'hidden',
            flex: '1',
          }}
        >
          {memoizedGroups}
        </Box>
      </EditFormScrollProvider>
    </ScrollRestoration>
  );
};

export default React.memo(Edit);
